// @ts-nocheck

import { Moment } from 'moment';
import {
	Currencies,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import { formatDate } from '@shared/utils/date';
import type { UserProps } from '@api/models/user';
import type { SupplierProps } from '@api/models/supplier';
import type { MarkFixtureAsFixedResponse } from '@api/features/fixtures/markFixtureAsFixed';
import { sendRequest, sendRequestWithFiles } from '@client/lib/sendRequest';

export const updateOrganization = (organizationId: number, attributes) => sendRequest(
	`/admin/organizations/${organizationId}`,
	'patch',
	{ attributes },
);

export const getWelcome = () => sendRequest('/');

export const getOrganizations = () => sendRequest('/admin/organizations');

export const createOrganization = (name: string, providerId: string) => sendRequest(
	'/admin/organizations',
	'post',
	{ name, providerId },
);

export const deleteOrganization = (organizationId: number) => sendRequest(
	`/admin/organizations/${organizationId}`,
	'delete',
);

export const getOrganizationUserDetails = (userId: number) => sendRequest<undefined, UserProps>(
	`/organization/users/${userId}`,
);

export const deleteVessel = (id: number) => sendRequest(`/vessels/${id}`, 'delete');

export const createVesselOwnershipPeriod = (vesselId: number, fromDate: Moment, toDate: Moment | null) => sendRequest(
	`/vessels/${vesselId}/ownership-periods`,
	'post',
	{ fromDate, toDate },
);

export const createVesselFinancialData = (vesselId: number, year: number | null,
	month: Date | Moment | string | null, capex: number, opex: number) => sendRequest(
	`/vessels/${vesselId}/financial-data`,
	'post',
	{ year, month, capex, opex },
);

export const updateVesselFinancialData = (vesselId: number, entryId: number, attributes) => sendRequest(
	`/vessels/${vesselId}/financial-data/${entryId}`,
	'patch',
	{ attributes },
);

export const deleteVesselFinancialData = (vesselId: number, entryId: number) => sendRequest(
	`/vessels/${vesselId}/financial-data/${entryId}`,
	'delete',
);

export const updateVesselOwnershipPeriod = (vesselId: number, ownershipPeriodId: number, attributes) => sendRequest(
	`/vessels/${vesselId}/ownership-periods/${ownershipPeriodId}`,
	'patch',
	{ attributes },
);

export const deleteVesselOwnershipPeriod = (vesselId: number, ownershipPeriodId: number) => sendRequest(
	`/vessels/${vesselId}/ownership-periods/${ownershipPeriodId}`,
	'delete',
);

export const getSupplierDetails = (supplierId: number) => sendRequest<undefined, SupplierProps>(
	`/suppliers/${supplierId}`,
);

export const createOffHirePeriod = (
	voyageId: number,
	startTime: Moment,
	endTime: Moment,
	preferDuration: boolean,
	bunkers,
	hirePerDay: number,
	percentageForOwnersAccount: number,
	note: string,
	invoiceNote: string,
	type: string | null,
	selectedTimeFormat: TimeZoneFormat,
	useInvoiceTimeFormat: boolean,
	attachments: File[],
) => sendRequestWithFiles(
	`/voyages/${voyageId}/off-hire`,
	'post',
	{
		startTime,
		endTime,
		preferDuration,
		bunkers,
		hirePerDay,
		percentageForOwnersAccount,
		note,
		invoiceNote,
		type,
		selectedTimeFormat,
		useInvoiceTimeFormat,
	},
	attachments,
);

// 'extra' is stuff like 'hireRate'
export const getHireInvoicePreview = (
	voyageId: number,
	hireInvoiceItems: HireInvoiceItem[],
	extra,
): Promise<{ html: string; total: number }> => sendRequest(
	'/invoices/hire-invoices/preview',
	'post',
	{
		voyageId,
		items: hireInvoiceItems,
		...(extra || {}),
	},
);

export const deleteHireInvoice = (id: number) => sendRequest(`/invoices/hire-invoices/${id}`, 'delete');

export const createVoyageExpense = (params: {
	voyageId: number;
	attachments: any; // TODO !
	itemDescription: string;
	customInvoiceId?: string | null;
	invoiceDate: Moment | null;
	dueDate: Moment | null;
	amount: number | null;
	note: string;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	supplierId: number;
	documentId?: number;
	isPortDa?: boolean;
	portCallId?: number;
	charterer?: number;
	estimated?: boolean;
	type?: 'payable' | 'payable_and_receivable';
	accountCodeOverride?: string | null;
	isFuelEuPenalty?: boolean;
	isEuEts?: boolean;
}) => {
	const data = new FormData();

	if (params.attachments != null) {
		params.attachments.forEach((a) => data.append('attachments', a));
	}

	const parameters = {
		...params,
		invoiceDate: params.invoiceDate && formatDate(params.invoiceDate),
		dueDate: params.dueDate && formatDate(params.dueDate),
	};

	// Add all not-null values to form data
	Object.entries(parameters).forEach(([key, value]) => {
		if (value != null) {
			data.append(key, value);
		}
	});

	return sendRequest('/invoices/voyage-expenses', 'post', data);
};

export const updateOrganizationLogo = (orgId: number, file) => {
	const data = new FormData();
	data.append('logo', file);

	return sendRequest(
		`admin/organizations/${orgId}/logo`,
		'post',
		data,
	);
};

export const updateVesselLogo = (id: number, file) => {
	const data = new FormData();
	data.append('logo', file);

	return sendRequest(
		`/vessels/${id}/logo`,
		'post',
		data,
	);
};

export const updateFixingCompanyLogo = (id: number, file?: File) => {
	const data = new FormData();

	if (file != null) {
		data.append('logo', file);
	}

	return sendRequest(
		`/fixing-companies/${id}/logo`,
		'post',
		data,
	);
};

export const submitExpense = (expenseId) => sendRequest(
	`/invoices/voyage-expenses/${expenseId}/submit`,
	'post',
);

export const unsubmitExpense = (expenseId) => sendRequest(
	`/invoices/voyage-expenses/${expenseId}/unsubmit`,
	'post',
);

export const getExchangeRates = (baseCurrency) => sendRequest(`/financials/exchange-rates/${baseCurrency}`);

export const markFixtureAsFixed = (id: number) => sendRequest<
	{ useOwnershipPeriod: boolean },
	MarkFixtureAsFixedResponse
>(`/fixtures/${id}/fix`, 'post', { useOwnershipPeriod: true });

export const markFixtureAsCompleted = (id) => sendRequest(`/fixtures/${id}/complete`, 'post');

export const resumeFixture = (id) => sendRequest(`/fixtures/${id}/resume`, 'post');

export const createFixture = (
	vesselId,
	counterpartyId,
	identifier,
	fixtureType,
	fixedBy,
	operatedBy,
	currency,
) => sendRequest(
	'/fixtures',
	'post',
	{
		vesselId,
		counterpartyId,
		identifier,
		fixtureType,
		fixedBy,
		operatedBy,
		currency,
	},
);

export const createExpenseSubjectToHireDays = (fixtureId, { name, amount, interval }) => sendRequest(
	`/fixtures/${fixtureId}/expenses-subject-to-hire-days`,
	'post',
	{ name, amount, interval },
);

export const deleteExpenseSubjectToHireDays = (tcTermsId, expenseId) => sendRequest(
	`/fixtures/${tcTermsId}/expenses-subject-to-hire-days/${expenseId}`,
	'delete',
);

export const addFixtureBroker = (tcTermsId, { brokerId, commission, paidBy }) => sendRequest(
	`/fixtures/${tcTermsId}/brokers`,
	'post',
	{ brokerId, commission, paidBy },
);

export const updateFixtureBroker = (tcTermsId, brokerId, attributes) => sendRequest(
	`/fixtures/${tcTermsId}/brokers/${brokerId}`,
	'patch',
	{ attributes },
);

export const removeFixtureBroker = (tcTermsId, brokerId) => sendRequest(
	`/fixtures/${tcTermsId}/brokers/${brokerId}`,
	'delete',
);
